import { memo, useContext, useState } from "react";
import { ObjectContext } from "@providers/ObjectProvider";
import Avatar from '@mui/joy/Avatar';
import Chip from '@mui/joy/Chip';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import ButtonGroup from '@mui/joy/ButtonGroup';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import CardActions from '@mui/joy/CardActions';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import SvgIcon from '@mui/joy/SvgIcon';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import Tooltip from '@mui/joy/Tooltip';
import Table from '@mui/joy/Table';
import Stack from '@mui/joy/Stack';
import ObjectRelationshipCard from '@components/objects/ObjectRelationshipCard';
import { gradientTint } from "@utils/gradientTint";

interface BaseColumn {
  label: string;
  class?: string;
}

interface ColumnWithValue extends BaseColumn {
  // label: string;
  value: string;//|number|null;
  // class?: string;
  transformer?: never;
} //& CommonColumn

interface ColumnWithTransformer extends BaseColumn {
  // label: string;
  value?: never;
  // class?: string;
  transformer: Function;
}

type IColumn = ColumnWithValue | ColumnWithTransformer;

const ObjectRelationshipTable = memo(function ObjectRelationshipTable({
  data = null, // parent object
  relationship = [],
  title,
  tint = null,
  sort = null,
  columns,
  collapsible = true,
  sx = null,
  action = null,
  callback = null,
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const perPage = 5;

  let sortedRelationship = relationship;
  if(typeof sort === 'function') {
    sortedRelationship = sortedRelationship.sort(sort);
  } else if(Array.isArray(sort)) {

    // const sortCol = sort[0];
    // const sortDir = sort[1];

    const sorters = Array.isArray(sort[0]) ? sort : [sort];

    sortedRelationship.sort((a, b) => {
      for(const [sortCol, sortDir] of sorters) {
        const sortA = sortDir == 'desc' ? b : a;
        const sortB = sortDir == 'desc' ? a : b;
        try {
          const ret = sortA[sortCol].localeCompare(sortB[sortCol]);
          if(ret != 0) {
            return ret;
          }
        } catch(e) {
          console.warn(e);
        }
      }

      return 0;

    });
    /*
    if(sortDir === 'desc') {
      sortedRelationship.reverse();
    }
    */
  }
  // const sortedRelationship = typeof sort === 'function' ? relationship.sort((a, b) => a.localeCompare(b)) : relationship;
  const paginatedRelationship = (isExpanded || !collapsible) ? sortedRelationship : sortedRelationship.slice(0, perPage);

  const canCollapse = collapsible && relationship.length > perPage;
  const isEmpty = paginatedRelationship.length === 0;

  let paginationTitle = null;

  if(collapsible && !isEmpty) {
    let titleText:any = 'All';
    if(relationship.length > perPage) {
      const ToggleIcon = isExpanded ? KeyboardArrowDownRoundedIcon : KeyboardArrowRightRoundedIcon;
      titleText = <>
        {`${isExpanded ? relationship.length : perPage} of ${relationship.length}`}
        <ToggleIcon sx={{width: '1rem', height: '1rem', mt: '-3px'}} />
      </>;
    }

    paginationTitle = (
      <Typography
        className="pagination"
        sx={theme => ({
          px: 1,
          py: 0.25,
          fontSize: 'sm',
          transition: 'var(--mm-default-transition)',
          backgroundColor: /*theme.palette.background.popup*/'#FFFFFF6B',// tint ? theme.palette[tint][200] : theme.palette.background.level2,
          borderRadius: 'sm',
          // todo: make text color the bg color of the card header
        })}
      >
        {titleText}
      </Typography>
    );
  }

  const shouldShowTableHeader = columns.filter(c => !!c.label).length > 0;

  return (
    <ObjectRelationshipCard
      unpadded
      empty={isEmpty}
      title={
        <CardOverflow
          sx={theme => ({
            borderTopLeftRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            borderTopRightRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            marginBlockStart: 'var(--CardOverflow-offset)',
            ...gradientTint(theme, tint),
          })}
        >
          <CardActions
            sx={theme => ({
              py: 0,//1.5,
              // pointerEvents: canCollapse ? 'all' : 'none',
              // cursor: 'pointer',
              userSelect: 'none',
              ':hover .pagination': {
                backgroundColor: '#FFFFFFB8',
              },
            })}
          >
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={3}
              width={'100%'}
            >
              <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={0.5}
                width={'100%'}
                sx={{
                  pointerEvents: canCollapse ? 'all' : 'none',
                  py: 1.5,
                  cursor: 'pointer',//canCollapse ? 'pointer' : 'text',
                }}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <Typography sx={theme => ({/*flex: 1,*/ fontWeight: '600', color: 'white' })}>{title}</Typography>
                {paginationTitle}
              </Stack>
              {action}
            </Stack>
          </CardActions>
        </CardOverflow>
      }
      sx={sx}
    >
      {/* <CardOverflow> */}
      <Table
        stickyHeader
        stripe="odd"
        // hoverRow
        sx={{
          margin: 0,
          padding: 0,
          tableLayout: 'auto',
          'thead th': {
            borderRadius: '0 !important',
          },
          /*
          'tbody td': {
            borderWidth: '0 !important',
          },
          */
        }}
      >
        {shouldShowTableHeader && (
          <thead>
            <tr>
              {columns.map((header:IColumn) => <th key={header.label} style={{width: header?.width}}>{header.label}</th>)}
            </tr>
          </thead>
        )}
        <tbody>
        {paginatedRelationship.map((row, rowIndex:number) => (
          <tr key={rowIndex}>
            {columns.map((cell:IColumn, cellIndex:number) => (
              <td key={`${rowIndex}-${cellIndex}`} className={cell?.class}>
                {cell.value && row[cell.value]}
                {cell?.render?.show && cell.render.show(row, data, callback)}
              </td>
            ))}
          </tr>
        ))}
        </tbody>
      </Table>
      {/* </CardOverflow> */}

    </ObjectRelationshipCard>
  );
});

export default ObjectRelationshipTable;
